import React from "react";
import { cfLogo } from "../../assets";
import { signInWithEmailAndPassword, signInWithPopup, isSignInWithEmailLink, signInWithEmailLink, sendPasswordResetEmail } from "firebase/auth";
import { auth, googleProvider } from "../../firebase";
import { setCookie } from "../../helper";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLogInUser, selectIsUserFromShopify } from "../../common/authenticateDashboard/authSlice.js"
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isReferrerShopify = useSelector(selectIsUserFromShopify);

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [googleLoading, setGoogleLoading] = React.useState(false);
  const [error, setError] = React.useState({
    email: null,
    password: null,
    server: null,
  });

  // Handle Magic Link verification if user is landing from a link in their inbox
  React.useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        email = window.prompt('Please provide your email for confirmation');
      }
      setLoading(true);
      signInWithEmailLink(auth, email, window.location.href)
        .then((result) => {
          window.localStorage.removeItem('emailForSignIn');
          const user = result.user.email;
          setCookie("user", user, 3);
          navigate("/");
        })
        .catch((err) => {
          setError({ ...error, server: "Magic link expired or invalid. Please request a new one." });
        })
        .finally(() => setLoading(false));
    }
  }, []);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);
      setError({ ...error, server: null });
      // Force account selector to prevent auto-login of revoked accounts
      googleProvider.setCustomParameters({
        prompt: 'select_account'
      });
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user.email;
      if (isReferrerShopify) {
        dispatch(setLogInUser(user));
      } else {
        setCookie("user", user, 3);
      }
      navigate("/");
    } catch (err) {
      setError({ ...error, server: "Google sign-in failed. Please try again." });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError({ ...error, server: null });
      // validate email and password
      if (!email && !password) {
        return setError({
          ...error,
          email: "Email is required",
          password: "Password is required",
        });
      }
      if (!email || !password) {
        email
          ? setError({ ...error, password: "Password is required" })
          : setError({ ...error, email: "Email is required" });
        return;
      }

      // login user data
      const userInfo = await signInWithEmailAndPassword(auth, email, password);
      const user = userInfo?.user?.email;

      // check if referer is shopify
      if (isReferrerShopify) {
        dispatch(setLogInUser(user))
      } else {
        setCookie("user", userInfo?.user?.email, 3);
      }
      navigate("/");
    } catch (err) {
      console.log(err);
      // Guide users who signed up via Google and never set a password
      const isGoogleOnlyUser =
        err.code === 'auth/wrong-password' ||
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/user-not-found';
      setError({
        ...error,
        server: isGoogleOnlyUser
          ? "Looks like you signed up with Google. Try 'Continue with Google' below, or click 'Set / Reset password' to create one!"
          : "Invalid login, Please try again!",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError({ ...error, email: "Please enter your email to reset password." });
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      alert("Password reset email sent! Check your inbox.");
    } catch (err) {
      console.error(err);
      alert("Failed to send reset email. Make sure the email is correct.");
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error)
      setError({
        ...error,
        email: null,
      });
  }

  const handlePasswordChange = (e) =>{
    setPassword(e.target.value);
    if (error)
      setError({
        ...error,
        password: null,
      });
  }

  return (
    <section className="login__page min-h-screen bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center">
      <div className="container p-4 sm:p-10 mx-auto text-neutral-800 dark:text-neutral-200 flex items-center justify-center">
        <div className="block rounded-lg bg-neutral-200 shadow-lg dark:bg-neutral-800">
          <div className="md:px-12">
            <div className="mx-6 py-12">
              {/* <!--Logo--> */}
              <div className="text-center">
                <img
                  className="mx-auto w-48 sm:w-64 md:w-80 mb-3"
                  src={cfLogo}
                  alt="logo"
                />
              </div>

              <form>
                <p className="mb-4 text-center">Please login to your account</p>
                {/* <!--Email input--> */}
                <div className="relative mb-4" data-te-input-wrapper-init>
                  <input
                    onChange={handleEmailChange}
                    type="email"
                    autoComplete="off"
                    className="peer block min-h-[auto] w-full bg-transparent px-3 py-[0.32rem] leading-[1.6] outline-none transition-all duration-200 ease-linear focus:placeholder:opacity-100 data-[te-input-state-active]:placeholder:opacity-100 motion-reduce:transition-none dark:placeholder:text-neutral-200 [&:not([data-te-input-placeholder-active])]:placeholder:opacity-0 border dark:!border-neutral-200 !border-neutral-800 rounded "
                    id="exampleFormControlInput1"
                    placeholder="Email"
                  />
                  <label
                    htmlFor="exampleFormControlInput1"
                    className={`pointer-events-none absolute left-3 top-0 mb-0 max-w-[90%] origin-[0_0] truncate pt-[0.37rem] leading-[1.6] text-neutral-500 transition-all duration-200 ease-out peer-focus:-translate-y-[0.9rem] peer-focus:scale-[0.8] peer-focus:text-primary peer-data-[te-input-state-active]:-translate-y-[0.9rem] peer-data-[te-input-state-active]:scale-[0.8] motion-reduce:transition-none dark:text-neutral-200 dark:peer-focus:text-primary peer-focus:bg-neutral-200 peer-focus:dark:bg-neutral-800 ${
                      email === ""
                        ? ""
                        : "-translate-y-[0.9rem] scale-[0.8] bg-color"
                    }`}
                  >
                    Email*
                  </label>
                  {/* error message */}
                  <div className="text-left max-w-xs">
                    <p className="text-xs text-red-500">
                      {error.email && error.email}
                    </p>
                  </div>
                </div>

                {/* <!--Password input--> */}
                <div className="relative mb-4" data-te-input-wrapper-init>
                  <input
                    onChange={handlePasswordChange}
                    type={passwordVisible ? "text" : "password"}
                    autoComplete="off"
                    className="peer block min-h-[auto] w-full bg-transparent px-3 py-[0.32rem] leading-[1.6] outline-none transition-all duration-200 ease-linear focus:placeholder:opacity-100 data-[te-input-state-active]:placeholder:opacity-100 motion-reduce:transition-none dark:placeholder:text-neutral-200 [&:not([data-te-input-placeholder-active])]:placeholder:opacity-0 border dark:!border-neutral-200 !border-neutral-800 rounded"
                    id="exampleFormControlInput11"
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-2 text-black text-xl"
                  >
                    {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                  </button>
                  <label
                    htmlFor="exampleFormControlInput11"
                    className={`pointer-events-none absolute left-3 top-0 mb-0 max-w-[90%] origin-[0_0] truncate pt-[0.37rem] leading-[1.6] text-neutral-500 transition-all duration-200 ease-out peer-focus:-translate-y-[0.9rem] peer-focus:scale-[0.8] peer-focus:text-primary peer-data-[te-input-state-active]:-translate-y-[0.9rem] peer-data-[te-input-state-active]:scale-[0.8] motion-reduce:transition-none dark:text-neutral-200 dark:peer-focus:text-primary peer-focus:bg-neutral-200 peer-focus:dark:bg-neutral-800 ${
                      password === ""
                        ? ""
                        : "-translate-y-[0.9rem] scale-[0.8] bg-color"
                    }`}
                  >
                    Password*
                  </label>
                  {/* error message */}
                  <div className="text-left max-w-xs">
                    <p className="text-xs text-red-500">
                      {error.password && error.password}
                    </p>
                  </div>
                </div>

                {/* <!--Forgot Password Link--> */}
                <div className="mb-4 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-xs text-primary transition duration-150 ease-in-out hover:text-primary-600 focus:text-primary-600 active:text-primary-700 dark:text-primary-400 dark:hover:text-primary-500 dark:focus:text-primary-500 dark:active:text-primary-600"
                  >
                    Set / Reset password
                  </button>
                </div>

                {/* <!--Submit button--> */}
                <div className="pb-1 pt-1 text-center">
                  <button
                    onClick={handleLogin}
                    className={`mb-3 inline-block w-full rounded px-6 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_rgba(0,0,0,0.2)] transition duration-150 ease-in-out hover:shadow-[0_8px_9px_-4px_rgba(0,0,0,0.1),0_4px_18px_0_rgba(0,0,0,0.2)] focus:shadow-[0_8px_9px_-4px_rgba(0,0,0,0.1),0_4px_18px_0_rgba(0,0,0,0.2)] focus:outline-none focus:ring-0 active:shadow-[0_8px_9px_-4px_rgba(0,0,0,0.1),0_4px_18px_0_rgba(0,0,0,0.2)] disabled:cursor-not-allowed disabled:opacity-70 ${
                      !loading && "pb-2 pt-2.5"
                    }`}
                    type="button"
                    data-te-ripple-init
                    data-te-ripple-color="light"
                    style={{
                      background:
                        "linear-gradient(to right, rgb(190 49 199), rgb(170 46 226), rgb(91 146 245), rgb(50 197 255))",
                    }}
                  >
                    {/* loading spinner svg */}
                    {loading ? (
                      <svg
                        className="mx-auto bg-transparent"
                        width="30px"
                        height="35px"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="xMidYMid"
                      >
                        <circle
                          cx="50"
                          cy="50"
                          r="0"
                          fill="none"
                          stroke="#ffffff"
                          strokeWidth="2"
                        >
                          <animate
                            attributeName="r"
                            repeatCount="indefinite"
                            dur="1s"
                            values="0;40"
                            keyTimes="0;1"
                            keySplines="0 0.2 0.8 1"
                            calcMode="spline"
                            begin="0s"
                          ></animate>
                          <animate
                            attributeName="opacity"
                            repeatCount="indefinite"
                            dur="1s"
                            values="1;0"
                            keyTimes="0;1"
                            keySplines="0.2 0 0.8 1"
                            calcMode="spline"
                            begin="0s"
                          ></animate>
                        </circle>
                        <circle
                          cx="50"
                          cy="50"
                          r="0"
                          fill="none"
                          stroke="#ffffff"
                          strokeWidth="2"
                        >
                          <animate
                            attributeName="r"
                            repeatCount="indefinite"
                            dur="1s"
                            values="0;40"
                            keyTimes="0;1"
                            keySplines="0 0.2 0.8 1"
                            calcMode="spline"
                            begin="-0.5s"
                          ></animate>
                          <animate
                            attributeName="opacity"
                            repeatCount="indefinite"
                            dur="1s"
                            values="1;0"
                            keyTimes="0;1"
                            keySplines="0.2 0 0.8 1"
                            calcMode="spline"
                            begin="-0.5s"
                          ></animate>
                        </circle>
                      </svg>
                    ) : (
                      <span>Login</span>
                    )}
                  </button>
                </div>

                {/* error message */}
                <div className="text-center max-w-xs">
                  <p className="text-xs text-red-500">
                    {error.server && error.server}
                  </p>
                </div>
              </form>

              {/* ── OR Divider ─────────────────────────────────────── */}
              <div className="flex items-center my-4">
                <div className="flex-1 border-t border-neutral-300 dark:border-neutral-600" />
                <span className="mx-3 text-xs text-neutral-400 uppercase tracking-widest">or</span>
                <div className="flex-1 border-t border-neutral-300 dark:border-neutral-600" />
              </div>

              {/* ── Google OAuth Button ─────────────────────────────── */}
              <button
                id="google-signin-btn"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="mb-3 w-full flex items-center justify-center gap-3 rounded border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-4 py-2.5 text-sm font-bold text-black dark:text-white shadow-sm hover:bg-neutral-50 dark:hover:bg-neutral-600 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <FcGoogle size={20} />
                {googleLoading ? "Signing in..." : "Sign in with Google"}
              </button>

              <p className="mt-2 text-center text-sm text-gray-500">
                Not a member?{" "}
                {/* <NavLink to="start-free-trial" className="font-semibold leading-6 text-indigo-600 hover:text-indigo-500">
                    Start a 14 day free trial
                </NavLink> */}
                <a href="https://www.causalfunnel.com/fix-my-conversion-funnel/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="font-semibold leading-6 text-indigo-600 hover:text-indigo-500">
                    Start a 14 day free trial
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
